/**
 * EVL-1 — `getRequirementLineage` consolidated helper.
 *
 * Live-prod symptom (Sickel 2026-05-18): 4 of 5 Coverage Matrix
 * requirements read "Strong" but the `[Author]` dialog opens
 * completely empty AND the row exposes no signal about WHICH sources
 * (DAM / providers / docs / authored / AI research / prior reports)
 * are making the cell Strong. The user's question — *"How do I know
 * what is being sent to the SED, reports etc with this section?"* —
 * has no answer surface.
 *
 * `getRequirementLineage` is the canonical projection: given a
 * requirement id + the input bundle (site, sed, reportType, dam,
 * matchByType, priorReports), it returns the FULL evidence chain
 * across the five tiers + the cross-cutting DAM / siteResearch
 * channels + a projection of what blocks reach the LLM for this
 * requirement.
 *
 * Pure projection — no I/O. Memoize at the caller (e.g. Coverage
 * Matrix row useMemo keyed on (site.id, sed.version, dam.version)).
 *
 * KEY INVARIANT — reuses `computeRequirementStatus` so the chip and
 * lineage view CANNOT disagree. The W66 trap (Documents-tab chip
 * counts diverging from Site-Evidence matrix) was caused by parallel
 * status definitions; EVL-1 follows UPE-3's discipline and routes
 * through the same status function.
 *
 * Composition (per Evidence_Lineage_Prompt.md §2):
 *   - UAE-2 (shipped, 0.99202): the `[Author]` button this lineage
 *     pre-fills sits on every Coverage row.
 *   - UPE (queued, 0.99214): `computeRequirementStatus` is the same
 *     tier-aware function.
 *   - RDX (queued, 0.99222): `priorReports` walks
 *     `report.reportClaims` / `sed.*.provenanceDetail.derivedFromReport`;
 *     until RDX wires the writer-side tap the array is empty.
 *   - SNT (queued, 0.99224): once flag lands, lineage filters
 *     `'degraded-instruction-leak'` slots out of `sedSlots`.
 *   - DCT (shipped, 0.99185): match scores feed Tier 3.
 *   - SSM (shipped, 0.99200): `report.scoringInfluence` participates.
 *   - KMU (shipped, 0.99184): KMZ-linked slots surface as
 *     `'verified-from-kmz'` source rows.
 */
import {
  computeRequirementStatus,
  type RequirementStatus,
} from '../coverage/requirementStatus';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';
import {
  REQUIREMENT_TO_FIELDS,
  sectionIdFromFieldPath,
  leafKeyFromFieldPath,
} from '../../config/requirementToFields';
import type {
  EvidenceSection,
  SedSectionId,
  SiteEvidenceDocument,
  StructuredEvidenceValue,
} from '../../types/siteEvidence';
import type { ExtractedClaim } from '../../types/siteContext';
import type { SiteDocument, SiteRagReport, DataSource } from '../../types/siteRag';
import type { ReportTypeId } from '../../config/reportTypes';
import type { DatacrateModel } from '../../types/datacrateModel';
import type {
  ChecklistItem,
  RankedChecklistMatch,
  RankedDocMatch,
} from '../../config/documentChecklists';
import { getDocumentChecklistFor } from '../../config/documentChecklists';

/* ── Provenance discriminator for SED slot rows ───────────────────── */

/**
 * Mirrors the union on `EvidenceSection.provenance` plus the KSD2
 * KMZ-derived path + an authored fall-through. The legacy
 * `'verified-from-kmz'` is mapped from `provenance === 'doc'` when
 * the slot's status carries the KMZ source tag.
 */
export type SedSlotProvenance =
  | 'provider'
  | 'doc'
  | 'doc-override'
  | 'provider+doc'
  | 'authored'
  | 'report-derived'
  | 'verified-from-kmz';

/* ── Per-slot lineage row ─────────────────────────────────────────── */

/**
 * One SED-slot contribution to a requirement. Coverage Matrix iterates
 * `REQUIREMENT_TO_SECTION_MAP[requirementId]` and emits one row per
 * populated slot. Drives both the Sources tab of
 * `EvidenceLineageInspector` (EVL-4) and the Author-dialog pre-fill
 * mapper (EVL-2).
 */
export interface SedSlotLineage {
  /** Section id from `REQUIREMENT_TO_SECTION_MAP` (e.g. `'grid'`). */
  sectionId: SedSectionId;
  /** Optional sub-field key when the slot exposes structured data
   *  (e.g. `'utilityName'` inside the `grid` slot). */
  fieldKey?: string;
  /** Human-readable label for UI surfaces. */
  fieldLabel?: string;
  /** Where the slot's value came from. */
  provenance: SedSlotProvenance;
  /** Canonical provider key when `provenance` includes provider data. */
  providerKey?: string;
  /** Human-readable provider label. */
  providerLabel?: string;
  /** Raw provider payload — typed as `unknown` so consumers narrow
   *  (the Author-dialog pre-fill mapper destructures this). */
  providerData?: unknown;
  /** ISO timestamp the provider returned (when known). */
  providerRetrievedAt?: string;
  /** Doc ids that contributed to the slot. Deduped. */
  docIds: string[];
  /** Doc filenames resolved at projection time so consumers don't
   *  need to re-look up `site.documents`. */
  docNames: string[];
  /** Confidence 0..1 — prefer `adjustedConfidence` (doc-aware) then
   *  fall back to `confidence`. */
  confidence: number;
  /** ≤200 char verbatim quotation when the section has one (REF-2). */
  evidenceText?: string;
  /** 1-based page when the doc-aware extractor recorded one. */
  pageNumber?: number;
  /** Slot status verbatim — lets the UI flag e.g. `'anomalous'` /
   *  `'verified-from-kmz'` separately from provenance. */
  status: string;
  /** CAR-6 — authored-schema field payload stamped on the slot when
   *  auto-fill ran (or when a higher tier preserved the detail). Used
   *  by Author-evidence populate (`seedFromLineage`). */
  autoFillFields?: Record<string, StructuredEvidenceValue>;
  /** CAR-6 — provider id for `autoFillFields` (e.g. `'nrcs-soil-survey'`). */
  autoFillProvider?: string;
}

/* ── DAM context ──────────────────────────────────────────────────── */

/**
 * Cross-cutting DAM channel. DAM REPLACES the inline KB / ADAMS /
 * benchmarks block in the report prompt when the report type is
 * `'feasibility'` (DM2 live-mode); for other report types it's
 * forward-looking but the projection still surfaces "DAM available"
 * so the user can see what curated context exists.
 */
export interface DamLineage {
  reportType: ReportTypeId;
  /** `'v0001'` etc — denotes immutable DAM version pointer. */
  version: string;
  /** True when the DAM's compiled block is being injected into the
   *  prompt for this reportType (VITE_DAM_LIVE_MODE gated). */
  isLive: boolean;
  /** DAM refs whose `field` overlaps with this requirement's section
   *  list. Filtered to keep "relevant" rows in the inspector — full
   *  ref list available on the DAM object itself. */
  relevantReferences: DataSource[];
}

/* ── Site research (deep-provider sweep) ──────────────────────────── */

export interface SiteResearchLineage {
  source: string;
  /** ISO timestamp the deep provider returned. */
  retrievedAt: string;
  /** Section ids under `'siteResearch.*'` this requirement consumes. */
  contributesTo: SedSectionId[];
}

/* ── LX-3 — Live-literature channel ───────────────────────────────── */

/**
 * LX-3 — `LLW`-orchestrator output projected for the lineage view.
 *
 * The LLW (live-literature) orchestrator fetches a small (≤12) set of
 * cross-provider papers per (site, reportType). These references are
 * NOT requirement-specific — the orchestrator's queries seed from
 * site state + reportType, not from individual checklist rows. So the
 * lineage projection surfaces ALL entries for the (site, reportType)
 * with a coarse `'live-literature'` channel rather than per-requirement
 * filtering (LL6 will refine this when the relevance scorer lands).
 *
 * Used by the CAF `'liveLit'` tier badge — surfaced as the WEAKEST
 * positive attribution (below `provider`) so it only renders when no
 * stronger tier carries the row.
 *
 * Hallucination guard: the badge tooltip surfaces the FIRST entry's
 * title + provider so the user can audit the source the live-literature
 * channel would feed into the report prompt.
 */
export interface LiveLiteratureLineage {
  /** Total entries returned by the orchestrator for (site, reportType). */
  entryCount: number;
  /** First (highest-relevance) entry — drives the badge tooltip. */
  topEntry?: {
    title: string;
    provider: 'crossref' | 'arxiv' | 'pubmed';
    url: string;
    year: number;
  };
  /** Orchestrator status — only 'ok' / 'partial' should drive the
   *  liveLit attribution. 'unavailable' leaves the channel undefined. */
  status: 'ok' | 'partial';
  /** Orchestrator fetch timestamp — surfaced in the badge tooltip. */
  fetchedAt: string;
}

/* ── Prior report channel (RDX-bridge) ────────────────────────────── */

export interface PriorReportLineage {
  reportId: string;
  reportType: ReportTypeId;
  /** Dot-path inside the SED that the prior report informed. */
  sedFieldPath: string;
  /** Raw value — `unknown` because RDX writes typed primitives. */
  value: unknown;
  /** 0..1 confidence from the report's claim layer. */
  confidence: number;
}

/* ── Prompt-inclusion projection ──────────────────────────────────── */

/**
 * What blocks of the report-generation prompt this requirement's
 * sources reach. Mirrors the 11-block composition in
 * `siteRagService.ts:3250-3251` WITHOUT rebuilding the prompt —
 * pure projection from inputs, suitable for the "Generation will
 * use" tab (EVL-5).
 *
 * `RefRow` indices are 1-based to match the report's `[Ref N]`
 * citation discipline.
 */
export interface PromptInclusion {
  /** SED sections serialized into the prompt body via `sedToText`. */
  sedTextSectionIds: SedSectionId[];
  /** `inputLayersBlock` fields (intent/typed-prefs/siteContext)
   *  visible to this requirement. */
  inputLayersFields: string[];
  /** Site-context claim ids serialized into `siteContextBlock`. */
  siteContextClaimIds: string[];
  /** `[Ref N]` indices resolving to DAM refs. */
  damReferenceRefIds: number[];
  /** `[Ref N]` indices resolving to live provider data. */
  providerReferenceRefIds: number[];
  /** `[Ref N]` indices resolving to user-uploaded docs. */
  docReferenceRefIds: number[];
}

/* ── Counts (chip strip) ──────────────────────────────────────────── */

export interface RequirementLineageCounts {
  damSources: number;
  providerSources: number;
  docSources: number;
  authoredClaims: number;
  siteResearchSources: number;
  priorReportSources: number;
}

/* ── Field-level coverage (EVL-6) ─────────────────────────────────── */

/**
 * EVL-6 — per-field coverage row. One entry per dot-path listed for
 * the requirement in `REQUIREMENT_TO_FIELDS`. Drives the Coverage
 * Matrix tooltip ("3/4 fields filled — missing voltage") and the
 * Author dialog field list (each Field key matches a leaf key).
 *
 * `filled` is `true` when `sed[section].data[leaf]` resolves to a
 * non-null / non-empty value. The walker is shape-tolerant: a `0`
 * is filled, an empty string / null / undefined / NaN is not.
 */
export interface RequirementFieldCoverageRow {
  fieldPath: string;
  sectionId: SedSectionId;
  leafKey: string;
  filled: boolean;
  /** Optional raw value — exposed for the Author-dialog pre-fill
   *  helper (EVL-2). `unknown` because the SED field types are
   *  heterogeneous (numbers, strings, booleans, arrays, structs). */
  value?: unknown;
}

export interface RequirementFieldCoverage {
  total: number;
  filled: number;
  rows: RequirementFieldCoverageRow[];
}

/* ── The top-level lineage shape ──────────────────────────────────── */

export interface RequirementLineage {
  requirementId: string;
  requirementLabel: string;
  reportType: ReportTypeId;
  /** Same status the Coverage Matrix chip renders. */
  status: RequirementStatus;
  /** Short-cut for "the chip says Strong". */
  isStrong: boolean;
  sources: {
    /** Tier 1 — user-authored claims for this requirement. */
    authored: ReadonlyArray<ExtractedClaim>;
    /** Tier 2 — populated SED slots covering the requirement's sections. */
    sedSlots: SedSlotLineage[];
    /** Tier 3 — DCT match fallback. */
    dctMatches: RankedDocMatch[];
    /** Cross-cutting DAM channel. */
    dam?: DamLineage;
    /** Cross-cutting site-research channel. */
    siteResearch?: SiteResearchLineage;
    /** RDX-bridge prior-report claims (empty until RDX wires the
     *  writer-side tap). */
    priorReports: PriorReportLineage[];
    /** LX-3 — cross-cutting live-literature channel. Present iff the
     *  LLW orchestrator returned `'ok'` / `'partial'` for the
     *  (site, reportType). Coarse — NOT per-requirement; the CAF
     *  `'liveLit'` tier renders only when no stronger tier carries
     *  the row. Undefined when `sed.externalLiterature` is missing
     *  or `'unavailable'`. */
    liveLiterature?: LiveLiteratureLineage;
  };
  promptInclusion: PromptInclusion;
  counts: RequirementLineageCounts;
  /** EVL-6 — sub-field coverage. Empty `rows` when the requirement
   *  isn't registered in `REQUIREMENT_TO_FIELDS`. Optional for
   *  back-compat with callers that synthesise a `RequirementLineage`
   *  literal (test fixtures, mocks); `getRequirementLineage` always
   *  populates it. */
  fieldCoverage?: RequirementFieldCoverage;
}

/* ── Inputs ───────────────────────────────────────────────────────── */

/**
 * Minimal site shape the projection reads. Kept narrow so the helper
 * can be unit-tested without standing up the full SiteEntity union.
 */
export interface RequirementLineageSiteInput {
  siteContext?: { claims?: ReadonlyArray<ExtractedClaim> } | null;
  documents?: ReadonlyArray<SiteDocument>;
}

export interface GetRequirementLineageInputs {
  site: RequirementLineageSiteInput;
  sed: SiteEvidenceDocument | undefined;
  reportType: ReportTypeId;
  dam?: DatacrateModel | null;
  /** Actual saved/planned scope of the one ranked run. When present, DCT
   *  sources come only from an in-scope checklist owning the requirement;
   *  the primary reportType still identifies DAM/prompt context. */
  scopeReportTypes?: readonly ReportTypeId[];
  /** Map of per-report-type ranked-match results from DCT. */
  matchByType?: ReadonlyMap<ReportTypeId, RankedChecklistMatch>;
  /** Prior reports on the site — RDX bridge (empty until RDX ships
   *  its writer-side reportClaims tap). */
  priorReports?: SiteRagReport[];
  /** Optional human-readable label override. When omitted, the
   *  caller's `requirementId` is used. */
  requirementLabel?: string;
  /** Optional checklist item — when supplied the projection uses the
   *  checklist's authoredSchema for slot mapping. */
  checklistItem?: ChecklistItem;
  /** DM2 live-mode gate per reportType. When `dam` is supplied but
   *  the reportType isn't live, the dam channel still surfaces as
   *  `isLive: false` (static fallback). */
  damIsLive?: boolean;
}

/* ── Helpers — humanisers ─────────────────────────────────────────── */

const SECTION_LABEL: Partial<Record<SedSectionId, string>> = {
  climate: 'Climate',
  waterStress: 'Water stress',
  seismic: 'Seismic',
  grid: 'Grid / utility',
  flood: 'Flood risk',
  environmental: 'Environmental',
  nuclearInfra: 'Nuclear infrastructure',
  solar: 'Solar resource',
  wind: 'Wind resource',
  electricity: 'Electricity tariff',
  airQuality: 'Air quality',
  connectivity: 'Connectivity / fiber',
  parcel: 'Parcel / land',
  regulatory: 'Regulatory stance',
  siteResearch: 'AI site research',
  adamsDocuments: 'NRC ADAMS docs',
  siteProfile: 'Site profile',
  onSiteInfrastructure: 'On-site infrastructure (KMZ)',
  externalLiterature: 'External literature',
};

function humanizeSectionId(id: SedSectionId): string {
  return SECTION_LABEL[id] ?? id;
}

function humanizeProviderKey(key: string | undefined): string {
  if (!key) return 'Provider';
  const k = key.toLowerCase().replace(/\s+/g, '-');
  if (k.includes('wri') || k.includes('aqueduct')) return 'WRI Aqueduct';
  if (k.includes('fema')) return 'FEMA NFHL';
  if (k.includes('nrel')) return 'NREL';
  if (k.includes('usgs')) return 'USGS';
  if (k.includes('eia')) return 'EIA';
  if (k.includes('hifld')) return 'HIFLD';
  if (k.includes('regrid')) return 'Regrid';
  if (k.includes('nws') || k.includes('noaa') || k.includes('open-meteo')) return 'NOAA / Open-Meteo';
  if (k.includes('peeringdb')) return 'PeeringDB';
  if (k.includes('airnow') || k.includes('aqi')) return 'AirNow';
  if (k.includes('usfws')) return 'USFWS';
  if (k.includes('nrc') || k.includes('adams')) return 'NRC ADAMS';
  return key;
}

/* ── Helpers — slot walk ──────────────────────────────────────────── */

function getSlot(
  sed: SiteEvidenceDocument | undefined,
  sectionId: SedSectionId,
): EvidenceSection<unknown> | undefined {
  if (!sed) return undefined;
  const all = sed as unknown as Record<string, EvidenceSection<unknown> | undefined>;
  return all[sectionId];
}

/**
 * Pulled from `requirementStatus.ts:isSlotPopulatedByDoc`. Duplicated
 * (not imported) because the latter is intentionally private to the
 * status module — duplication is safer than widening the surface API.
 */
function isSlotPopulated<T>(slot: EvidenceSection<T> | undefined): boolean {
  if (!slot) return false;
  if (slot.status === 'unavailable') return false;
  if ((slot.linkedDocumentIds ?? []).length > 0) return true;
  if ((slot.linkedDocumentRefs ?? []).length > 0) return true;
  if (slot.status === 'verified-from-doc') return true;
  if (slot.status === 'verified-from-context') return true;
  if (slot.status === 'verified-from-report') return true;
  // CAF (2026-05-18) — DCO-stamped tier statuses. These are populated
  // BY DEFINITION (the orchestrator writes a non-null `data` payload
  // before flipping the status). EVL-1 shipped before DCO Wave-A/B
  // widened the EvidenceStatus union, so these were silently filtered
  // out of `sedSlots[]`, which meant `<CoverageTierBadge>` and the
  // EVL-3 lineage chip strip never surfaced DAM/Research fills. Customer
  // trust regression: "5/5 missing" copy on a SED the orchestrator
  // had already filled.
  if (slot.status === 'verified-from-dam') return true;
  if (slot.status === 'verified-from-research') return true;
  // Provider-only verified slot — still counts as a contribution to
  // the lineage view even though Tier 2 inside `computeRequirementStatus`
  // requires doc participation. EVL surfaces ALL sources, the status
  // function decides the chip.
  if (slot.status === 'verified') return true;
  if (slot.status === 'anomalous') return true;
  // KSD1 supplemental status — present on `onSiteInfrastructure` only.
  if ((slot.status as string) === 'verified-from-kmz') return true;
  return false;
}

function resolveDocNames(
  ids: ReadonlyArray<string>,
  documents: ReadonlyArray<SiteDocument> | undefined,
): string[] {
  if (!documents || documents.length === 0) return [];
  const byId = new Map(documents.map(d => [d.id, d.fileName ?? d.id]));
  const out: string[] = [];
  for (const id of ids) {
    const name = byId.get(id);
    if (name) out.push(name);
  }
  return out;
}

/**
 * Map an `EvidenceSection`'s `provenance` + `status` to the union
 * `SedSlotLineage.provenance`. KMZ-stamped slots take precedence over
 * the `provenance` field (KSD2 stamps `'doc'` on KMZ-derived slots so
 * the legacy doc handlers still match).
 */
function slotProvenance(slot: EvidenceSection<unknown>): SedSlotProvenance {
  if ((slot.status as string) === 'verified-from-kmz') return 'verified-from-kmz';
  if (slot.status === 'verified-from-context') return 'authored';
  if (slot.status === 'verified-from-report') return 'report-derived';
  const p = slot.provenance;
  if (p === 'doc') return 'doc';
  if (p === 'doc-override') return 'doc-override';
  if (p === 'provider+doc') return 'provider+doc';
  if (p === 'report-derived' || p === 'report-overrides-provider') return 'report-derived';
  if (p === 'report+provider') return 'provider+doc';
  return 'provider';
}

/**
 * Project one SED slot into its lineage row. Returns `undefined` when
 * the slot isn't populated — the caller filters those out.
 */
function projectSlotLineage(
  sectionId: SedSectionId,
  slot: EvidenceSection<unknown> | undefined,
  documents: ReadonlyArray<SiteDocument> | undefined,
): SedSlotLineage | undefined {
  if (!slot || !isSlotPopulated(slot)) return undefined;
  const provenance = slotProvenance(slot);
  const docIdsSet = new Set<string>();
  for (const id of slot.linkedDocumentIds ?? []) docIdsSet.add(id);
  for (const id of slot.supportingDocIds ?? []) docIdsSet.add(id);
  for (const ref of slot.linkedDocumentRefs ?? []) {
    if (ref?.docId) docIdsSet.add(ref.docId);
  }
  const docIds = Array.from(docIdsSet);
  const docNames = resolveDocNames(docIds, documents);
  // Page + evidenceText come from the strongest linkedDocumentRef
  // (highest score) when present — drives the "Page 42" link.
  let pageNumber: number | undefined;
  let evidenceText: string | undefined;
  const refs = (slot.linkedDocumentRefs ?? []).slice().sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));
  for (const ref of refs) {
    if (pageNumber === undefined && typeof ref.pageNumber === 'number') {
      pageNumber = ref.pageNumber;
    }
    if (!evidenceText && ref.excerptPreview) {
      evidenceText = ref.excerptPreview;
    }
    if (pageNumber !== undefined && evidenceText) break;
  }
  return {
    sectionId,
    fieldLabel: humanizeSectionId(sectionId),
    provenance,
    providerKey: slot.source,
    providerLabel: humanizeProviderKey(slot.source),
    providerData: slot.data,
    providerRetrievedAt: slot.retrievedAt,
    docIds,
    docNames,
    confidence: slot.adjustedConfidence ?? slot.confidence ?? 0,
    evidenceText,
    pageNumber,
    status: slot.status,
    autoFillFields: slot.provenanceDetail?.autoFillFields,
    autoFillProvider: slot.provenanceDetail?.autoFillProvider,
  };
}

/* ── Helpers — DAM relevance filter ───────────────────────────────── */

/**
 * Heuristic DAM-ref relevance filter (R6 in the prompt pack). A DAM
 * ref's `field` is matched against the requirement's section list
 * by section-prefix membership; a future `DAM_REF_TO_SECTION_MAP`
 * can override per-ref.
 */
function damRefMatchesRequirement(
  ref: DataSource,
  sectionIds: ReadonlyArray<SedSectionId>,
): boolean {
  const field = (ref.field ?? '').toLowerCase();
  if (!field) return false;
  for (const sid of sectionIds) {
    const prefix = sid.toLowerCase().split('.')[0];
    if (prefix && field.includes(prefix)) return true;
  }
  return false;
}

/* ── Helpers — prior-report bridge ────────────────────────────────── */

/**
 * RDX bridge. Walks `sed.*.provenanceDetail.derivedFromReport` (the
 * shape RDX-1 writes) AND `report.reportClaims` (forward-looking)
 * for any slot covered by `sectionIds`. Empty until RDX ships its
 * writer-side tap.
 */
function extractPriorReportLineage(
  sectionIds: ReadonlyArray<SedSectionId>,
  sed: SiteEvidenceDocument | undefined,
  priorReports: ReadonlyArray<SiteRagReport> | undefined,
): PriorReportLineage[] {
  const out: PriorReportLineage[] = [];
  const seen = new Set<string>();
  if (sed) {
    for (const sid of sectionIds) {
      const slot = getSlot(sed, sid);
      const derived = slot?.provenanceDetail?.derivedFromReport;
      if (!derived) continue;
      const key = `${derived.reportId}::${sid}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        reportId: derived.reportId,
        reportType: derived.reportType,
        sedFieldPath: `${sid}.${derived.sectionId}`,
        value: slot?.data,
        confidence: slot?.adjustedConfidence ?? slot?.confidence ?? 0.6,
      });
    }
  }
  // Forward-compat: walk priorReports[].reportClaims when the tap
  // lands. Today RDX writes the lineage via SED provenance only;
  // this loop guards against double-counting via `seen`.
  for (const r of priorReports ?? []) {
    const claims = (r as unknown as { reportClaims?: Array<{ sedFieldPath?: string; value?: unknown; confidence?: number }> })
      .reportClaims;
    if (!claims) continue;
    for (const c of claims) {
      if (!c?.sedFieldPath) continue;
      const sectionPrefix = c.sedFieldPath.split('.')[0];
      if (!sectionPrefix) continue;
      if (!sectionIds.includes(sectionPrefix as SedSectionId)) continue;
      const key = `${r.id}::${c.sedFieldPath}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        reportId: r.id,
        reportType: r.reportType as ReportTypeId,
        sedFieldPath: c.sedFieldPath,
        value: c.value,
        confidence: typeof c.confidence === 'number' ? c.confidence : 0.6,
      });
    }
  }
  return out;
}

/* ── Helpers — prompt-inclusion projection ────────────────────────── */

/**
 * Map a requirement id to the `inputLayersBlock` fields its sources
 * touch. The full mapping mirrors `siteRagService.ts` block
 * composition; we expose the requirement-level slice the user cares
 * about for the Generation tab.
 */
function inputLayersFieldsFor(requirementId: string): string[] {
  switch (requirementId) {
    case 'utility-letter':
    case 'iso-queue':
      return ['intent.power.target_MW', 'siteContext.gridAssumptions'];
    case 'tariff':
      return ['intent.power.expected_price', 'siteContext.tariffPreferences'];
    case 'parcel-survey':
    case 'alta-survey':
    case 'mineral-title':
    case 'survey':
    case 'utility-locate':
      return ['intent.siteSize.acres', 'siteContext.parcelNotes'];
    case 'zoning':
      return ['intent.zoning.compatibility', 'siteContext.zoningContext'];
    case 'flood-cert':
      return ['intent.risk.acceptedFloodZones'];
    case 'geotech':
      return ['intent.foundation.minBearing_kPa'];
    case 'phase1-esa':
    case 'wetland':
    case 'species-survey':
    case 'cultural':
    case 'archaeological-survey':
      return [
        'intent.environmental.tolerance',
        'siteContext.environmentalNotes',
        'typedPreferences.environmental.expectedNepaPathway',
        'typedPreferences.environmental.federalNexus',
        'typedPreferences.environmental.speciesConcerns',
        'typedPreferences.environmental.tribalLandProximityKnown',
        'typedPreferences.environmental.airBasinOverride',
        'typedPreferences.environmental.dischargeWaterTarget',
      ];
    case 'water-rights':
    case 'aquifer-study':
      return ['intent.water.demand_MGD', 'siteContext.waterAssumptions'];
    case 'discharge-permit':
      return [
        'intent.water.demand_MGD',
        'siteContext.waterAssumptions',
        'typedPreferences.environmental.dischargeWaterTarget',
      ];
    case 'fiber-survey':
    case 'latency-test':
      return ['intent.connectivity.latency_target_ms'];
    default:
      return [];
  }
}

/**
 * `computePromptInclusion` mirrors how `siteRagService.ts:3250-3251`
 * composes the prompt body, returning which blocks/refs WOULD be
 * available to the LLM for `requirementId` WITHOUT rebuilding the
 * prompt. Reproducible from inputs alone. Unit-tested.
 */
export function computePromptInclusion(
  requirementId: string,
  sectionIds: ReadonlyArray<SedSectionId>,
  authored: ReadonlyArray<ExtractedClaim>,
  dam: DamLineage | undefined,
  sedSlots: ReadonlyArray<SedSlotLineage>,
): PromptInclusion {
  // SED text blocks — exactly the sections this requirement covers,
  // intersected with the slots that are actually populated (the LLM
  // never sees an empty section).
  const sedTextSectionIds: SedSectionId[] = Array.from(
    new Set(sedSlots.map(s => s.sectionId)),
  );
  // If the requirement has sections in the map but none are
  // populated, fall back to the full section list — those sections
  // are still placeholdered in the prompt (the LLM is told they're
  // unavailable). Keeps the projection honest about what reaches
  // the LLM even when the answer is "we have nothing".
  if (sedTextSectionIds.length === 0 && sectionIds.length > 0) {
    sedTextSectionIds.push(...sectionIds);
  }
  // 1-based Ref indices — we don't have the actual references[]
  // array here so we surface placeholder ranges. The Generation tab
  // (EVL-5) resolves these against `report.references` at render
  // time.
  const damReferenceRefIds: number[] = dam?.relevantReferences.map((_, i) => i + 1) ?? [];
  const providerReferenceRefIds: number[] = sedSlots
    .filter(s => s.provenance === 'provider' || s.provenance === 'provider+doc')
    .map((_, i) => i + 1);
  const docReferenceRefIds: number[] = Array.from(
    new Set(sedSlots.flatMap(s => s.docIds)),
  ).map((_, i) => i + 1);
  return {
    sedTextSectionIds,
    inputLayersFields: inputLayersFieldsFor(requirementId),
    siteContextClaimIds: authored.map(c => c.id),
    damReferenceRefIds,
    providerReferenceRefIds,
    docReferenceRefIds,
  };
}

/* ── Field-level coverage walker (EVL-6) ──────────────────────────── */

/**
 * EVL-6 — walk a requirement's registered field paths against the
 * live SED and report per-field fill state. Pure projection, no I/O.
 *
 * Fill rule:
 *   - Numbers: filled iff finite (`Number.isFinite`). `0` counts.
 *   - Booleans: filled (presence is the answer).
 *   - Strings: filled iff non-empty after trim.
 *   - Arrays: filled iff `length > 0`.
 *   - Objects: filled iff present (non-null).
 *   - Null / undefined / NaN: NOT filled.
 *
 * Whole-section paths (no dot) check whether the section's `.data`
 * struct exists AND is non-empty (at least one own enumerable key).
 *
 * Empty registration ⇒ empty rows. Callers can pivot on
 * `coverage.total === 0` to fall back to section-level chips.
 */
export function computeRequirementFieldCoverage(
  requirementId: string,
  sed: SiteEvidenceDocument | undefined,
): RequirementFieldCoverage {
  const fields = REQUIREMENT_TO_FIELDS[requirementId] ?? [];
  if (fields.length === 0 || !sed) {
    return { total: 0, filled: 0, rows: [] };
  }
  const rows: RequirementFieldCoverageRow[] = [];
  for (const fp of fields) {
    const sid = sectionIdFromFieldPath(fp);
    const leaf = leafKeyFromFieldPath(fp);
    const slot = getSlot(sed, sid as SedSectionId);
    const data = slot?.data as Record<string, unknown> | undefined;
    let value: unknown = undefined;
    let filled = false;
    if (fp === sid) {
      // whole-section read — `data` itself must be a non-empty object.
      if (data && typeof data === 'object') {
        const keyCount = Object.keys(data).length;
        if (keyCount > 0) {
          filled = true;
          value = data;
        }
      }
    } else if (data && typeof data === 'object') {
      value = data[leaf];
      filled = isFieldFilled(value);
    }
    rows.push({
      fieldPath: fp,
      sectionId: sid as SedSectionId,
      leafKey: leaf,
      filled,
      value,
    });
  }
  return {
    total: rows.length,
    filled: rows.filter(r => r.filled).length,
    rows,
  };
}

function isFieldFilled(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'boolean') return true;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as Record<string, unknown>).length > 0;
  return false;
}

/* ── EVL-5-RESOLVED — verbatim prompt-slice resolution ────────────── */

/**
 * Maps `SedSectionId` to the ALL-CAPS section title `sedToText`
 * emits as the `## TITLE [STATUS — source]` header. Drives
 * `sliceSedTextForSection` — without this map we'd have to grep the
 * full prompt body by id, which doesn't match how the serializer
 * spells them ("GRID INFRASTRUCTURE" not "grid").
 *
 * The map is deliberately narrow (only sections we currently
 * route requirements through). Unknown ids fall back to a header
 * derived from the id, which still finds the right block when
 * `sedToText` is extended in lock-step.
 */
const SED_SECTION_HEADER: Partial<Record<SedSectionId, string>> = {
  climate: 'CLIMATE',
  waterStress: 'WATER STRESS',
  seismic: 'SEISMIC',
  grid: 'GRID INFRASTRUCTURE',
  flood: 'FLOOD RISK',
  environmental: 'ENVIRONMENTAL',
  nuclearInfra: 'NUCLEAR INFRASTRUCTURE',
  solar: 'SOLAR RESOURCE',
  wind: 'WIND RESOURCE',
  electricity: 'ELECTRICITY PRICING',
  airQuality: 'AIR QUALITY',
  connectivity: 'CONNECTIVITY',
  parcel: 'PARCEL DATA',
  regulatory: 'REGULATORY',
  siteResearch: 'SITE-SPECIFIC RESEARCH',
  siteProfile: 'SITE PROFILE',
};

/**
 * EVL-5-RESOLVED — a resolved prompt-body slice. One per
 * channel × requirement; the inspector tab renders these in
 * <pre> blocks.
 */
export interface PromptSlice {
  /** Stable identifier for testid + react keys. */
  kind:
    | 'sed-section'
    | 'input-layers'
    | 'site-context'
    | 'dam-ref'
    | 'provider-ref'
    | 'doc-ref';
  /** Human-readable label — e.g. "GRID INFRASTRUCTURE", "Authored claims". */
  label: string;
  /** Verbatim slice that would reach the LLM. */
  text: string;
  /** Token estimate using the standard `chars / 4` heuristic
   *  (industry-typical for English/Latin script). Never negative. */
  estimatedTokens: number;
  /** Optional sub-id when the slice targets a specific [Ref N] or
   *  field path — e.g. `'grid.utilityName'`, `'[Ref 2]'`. */
  refKey?: string;
}

/** Token estimator — `chars / 4` is the rule-of-thumb for English
 *  prompts. Rounds up so an empty slice still reads as 0 tokens
 *  (not negative). */
export const PROMPT_CHARS_PER_TOKEN = 4;

export function estimateTokens(text: string): number {
  if (!text || text.length === 0) return 0;
  const t = Math.ceil(text.length / PROMPT_CHARS_PER_TOKEN);
  return Number.isFinite(t) && t > 0 ? t : 0;
}

/**
 * Scan a `sedToText` output for the `## TITLE [STATUS — src]` block
 * matching `header`. Returns the verbatim block (header + body up to
 * the next `##` header or EOF), or `undefined` when not found.
 *
 * Pure string scan — no regex backtracking. Safe on multi-line bodies.
 */
export function sliceSedTextForHeader(
  sedText: string,
  header: string,
): string | undefined {
  if (!sedText || !header) return undefined;
  const lines = sedText.split('\n');
  const startIdx = lines.findIndex(l => l.startsWith(`## ${header}`));
  if (startIdx === -1) return undefined;
  let endIdx = lines.length;
  for (let i = startIdx + 1; i < lines.length; i++) {
    if (lines[i]!.startsWith('## ')) {
      endIdx = i;
      break;
    }
  }
  // Trim trailing blank lines inside the block so consecutive slices
  // don't render with double whitespace.
  let block = lines.slice(startIdx, endIdx).join('\n');
  block = block.replace(/\n+$/, '');
  return block;
}

/**
 * Resolve a single SedSectionId to its verbatim block in `sedText`.
 * Falls back to the section id itself when no header mapping exists
 * (forward-compat for sections added after EVL ships).
 */
export function sliceSedTextForSection(
  sedText: string,
  sectionId: SedSectionId,
): string | undefined {
  const header = SED_SECTION_HEADER[sectionId] ?? sectionId.toUpperCase();
  return sliceSedTextForHeader(sedText, header);
}

/**
 * EVL-5-RESOLVED — resolve the `PromptInclusion` projection into
 * verbatim slices. The Generation tab + "What the LLM saw" tab of
 * `RequirementLineageInspector` render these slices in <pre> blocks
 * with per-slice copy-to-clipboard and token estimates.
 *
 * The function is pure — no I/O, no network. It only needs the
 * already-built `sedText` (cheap to compute via `sedToText(sed)` once
 * at the call site and pass through; the resolver does NOT re-run
 * `sedToText`).
 *
 * When `sedText` is omitted, the SED-section slices fall back to a
 * structured placeholder so the inspector tab still renders without
 * accidentally suggesting the section is absent from the prompt.
 *
 * @param requirementId  Coverage Matrix row id (drives the input-layers list).
 * @param sectionIds     The same section list `getRequirementLineage`
 *                       walks (`REQUIREMENT_TO_SECTION_MAP[req]`).
 * @param authored       Authored claims for the requirement.
 * @param dam            Optional DAM lineage with the relevant refs.
 * @param sedSlots       Lineage rows for populated SED slots.
 * @param sedText        Full output of `sedToText(sed)` — sliced here
 *                       per section. Omit to fall back to placeholders.
 */
export function resolvePromptInclusion(
  requirementId: string,
  sectionIds: ReadonlyArray<SedSectionId>,
  authored: ReadonlyArray<ExtractedClaim>,
  dam: DamLineage | undefined,
  sedSlots: ReadonlyArray<SedSlotLineage>,
  sedText?: string,
): PromptSlice[] {
  const out: PromptSlice[] = [];
  // 1) SED-section slices — verbatim chunks of `sedToText` for the
  //    requirement's sections that are actually populated. Empty
  //    section list falls back to the full list so the user sees the
  //    placeholder header the LLM would see.
  const sectionList: SedSectionId[] = Array.from(
    new Set(sedSlots.map(s => s.sectionId)),
  );
  const effectiveSections =
    sectionList.length > 0 ? sectionList : [...sectionIds];
  for (const sid of effectiveSections) {
    const slice =
      typeof sedText === 'string'
        ? sliceSedTextForSection(sedText, sid)
        : undefined;
    const header = SED_SECTION_HEADER[sid] ?? sid.toUpperCase();
    const text =
      slice ??
      `## ${header} [UNRESOLVED — pass sedText to resolvePromptInclusion to materialize this slice]`;
    out.push({
      kind: 'sed-section',
      label: header,
      text,
      estimatedTokens: estimateTokens(text),
      refKey: sid,
    });
  }
  // 2) Input-layers — re-use the requirement-level mapping. We render
  //    the path list as a single block so the tab stays compact.
  const inputLayerFields = inputLayersFieldsFor(requirementId);
  if (inputLayerFields.length > 0) {
    const text = `## RESOLVED USER CONFIG (inputLayersBlock)\n${inputLayerFields.map(f => `  - ${f}`).join('\n')}`;
    out.push({
      kind: 'input-layers',
      label: 'Resolved user config',
      text,
      estimatedTokens: estimateTokens(text),
    });
  }
  // 3) Site-context — serialized authored claims (matches the SC8
  //    block siteRagService injects). One slice per claim for
  //    per-claim copy.
  for (const claim of authored) {
    const evidenceText = claim.claim || claim.sourceSpan || '(no claim text)';
    const fields = claim.structuredFields
      ? Object.entries(claim.structuredFields)
          .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
          .join('\n  - ')
      : '';
    const body =
      `### Authored claim — ${claim.id}` +
      `\n  - kind: ${claim.authoredKind ?? 'prose'}` +
      `\n  - confidence: ${Math.round((claim.extractorConfidence ?? 0) * 100)}%` +
      (fields ? `\n  - ${fields}` : '') +
      `\n  - "${evidenceText}"`;
    out.push({
      kind: 'site-context',
      label: `Authored claim · ${claim.id}`,
      text: body,
      estimatedTokens: estimateTokens(body),
      refKey: claim.id,
    });
  }
  // 4) DAM refs — verbatim refs filtered by relevance.
  if (dam && dam.relevantReferences.length > 0) {
    dam.relevantReferences.forEach((ref, i) => {
      const text =
        `[Ref ${i + 1}] ${ref.source}` +
        (ref.field ? `\n  field: ${ref.field}` : '') +
        (ref.retrievedAt ? `\n  retrieved: ${ref.retrievedAt}` : '') +
        `\n  channel: Datacrate Model ${dam.version}${dam.isLive ? ' (live)' : ' (static)'}`;
      out.push({
        kind: 'dam-ref',
        label: `DAM [Ref ${i + 1}]`,
        text,
        estimatedTokens: estimateTokens(text),
        refKey: `[Ref ${i + 1}]`,
      });
    });
  }
  // 5) Provider refs — one slice per provider-bearing slot.
  const providerSlots = sedSlots.filter(
    s => s.provenance === 'provider' || s.provenance === 'provider+doc',
  );
  providerSlots.forEach((slot, i) => {
    const text =
      `[Ref ${i + 1}] ${slot.providerLabel ?? slot.providerKey ?? 'Provider'}` +
      `\n  section: ${slot.sectionId}${slot.fieldLabel ? ` (${slot.fieldLabel})` : ''}` +
      (slot.providerRetrievedAt ? `\n  retrieved: ${slot.providerRetrievedAt}` : '') +
      `\n  confidence: ${Math.round((slot.confidence ?? 0) * 100)}%` +
      (slot.evidenceText ? `\n  evidence: "${slot.evidenceText}"` : '');
    out.push({
      kind: 'provider-ref',
      label: `Provider · ${slot.providerLabel ?? slot.providerKey ?? slot.sectionId}`,
      text,
      estimatedTokens: estimateTokens(text),
      refKey: `[Ref ${i + 1}]`,
    });
  });
  // 6) Doc refs — one slice per unique doc.
  const seenDoc = new Set<string>();
  let docIdx = 0;
  for (const slot of sedSlots) {
    for (let j = 0; j < slot.docIds.length; j++) {
      const id = slot.docIds[j]!;
      if (seenDoc.has(id)) continue;
      seenDoc.add(id);
      docIdx += 1;
      const name = slot.docNames[j] ?? id;
      const text =
        `[Ref ${docIdx}] ${name}` +
        `\n  docId: ${id}` +
        `\n  contributesTo: ${slot.sectionId}${slot.pageNumber ? ` (page ${slot.pageNumber})` : ''}` +
        (slot.evidenceText ? `\n  evidence: "${slot.evidenceText}"` : '');
      out.push({
        kind: 'doc-ref',
        label: `Doc · ${name}`,
        text,
        estimatedTokens: estimateTokens(text),
        refKey: `[Ref ${docIdx}]`,
      });
    }
  }
  return out;
}

/* ── Top-level helper ─────────────────────────────────────────────── */

/** Select one requirement-owning analysis, without combining unrelated
 *  matches or changing the primary DAM's attribution. The first owning type
 *  preserves legacy shared-requirement behavior; an explicit empty/shrunken
 *  scope cannot recover a stale match from the rest of matchByType. */
export function resolveRequirementDocumentMatchType(
  requirementId: string,
  scopeReportTypes: readonly ReportTypeId[],
): ReportTypeId | undefined {
  return scopeReportTypes.find(type =>
    getDocumentChecklistFor(type).some(item => item.id === requirementId));
}

/**
 * Project a requirement's full evidence chain. Pure. Never throws.
 *
 * @param requirementId  Coverage Matrix row id (`ChecklistItem.id`).
 * @param inputs         Site + SED + reportType + optional DAM /
 *                       matches / prior reports.
 */
export function getRequirementLineage(
  requirementId: string,
  inputs: GetRequirementLineageInputs,
): RequirementLineage {
  const { site, sed, reportType, dam, matchByType, priorReports, requirementLabel, checklistItem, damIsLive } = inputs;
  const sectionIds: ReadonlyArray<SedSectionId> = REQUIREMENT_TO_SECTION_MAP[requirementId] ?? [];
  const documents = site.documents ?? [];

  // ── Tier 1 — authored claims ───────────────────────────────────
  const authored = (site.siteContext?.claims ?? []).filter(
    c => c.requirementId === requirementId,
  );

  // ── Tier 2 — SED slot lineage ──────────────────────────────────
  const sedSlots: SedSlotLineage[] = [];
  for (const sid of sectionIds) {
    const projection = projectSlotLineage(sid, getSlot(sed, sid), documents);
    if (projection) sedSlots.push(projection);
  }

  // ── Tier 3 — DCT match fallback (kept for the "matched docs"
  //            row in the modal, NOT for status). ─────────────────
  const dctMatches: RankedDocMatch[] = [];
  const documentMatchType = inputs.scopeReportTypes === undefined
    ? reportType
    : resolveRequirementDocumentMatchType(requirementId, inputs.scopeReportTypes);
  const match = documentMatchType === undefined ? undefined : matchByType?.get(documentMatchType);
  if (match) {
    const rows = match.matched.get(requirementId);
    if (rows && rows.length > 0) dctMatches.push(...rows);
  }

  // ── Status (single source of truth) ────────────────────────────
  // Reuses `computeRequirementStatus` so the chip and lineage CAN'T
  // disagree (W66 invariant).
  const requirement: ChecklistItem = checklistItem ?? {
    id: requirementId,
    label: requirementLabel ?? requirementId,
    description: '',
    sedSectionsHelpful: [...sectionIds],
    relevanceTagPattern: [],
    severity: 'recommended',
  };
  const status = computeRequirementStatus({
    requirement,
    sed,
    documents,
    authoredClaims: authored,
    match,
  });

  // ── DAM — only refs whose field overlaps with the requirement's
  //          sections. ───────────────────────────────────────────
  const damLineage: DamLineage | undefined = dam
    ? {
        reportType,
        version: typeof dam.version === 'number' ? `v${String(dam.version).padStart(4, '0')}` : String(dam.version ?? 'v0001'),
        isLive: damIsLive ?? (reportType === 'feasibility'),
        relevantReferences: (dam.references ?? []).filter(r => damRefMatchesRequirement(r, sectionIds)),
      }
    : undefined;

  // ── Site research — only when the requirement's sections include
  //                    `'siteResearch'`. ────────────────────────
  let siteResearch: SiteResearchLineage | undefined;
  const siteResearchSlot = getSlot(sed, 'siteResearch');
  if (sectionIds.includes('siteResearch') && siteResearchSlot && isSlotPopulated(siteResearchSlot)) {
    siteResearch = {
      source: siteResearchSlot.source ?? 'deep-research',
      retrievedAt: siteResearchSlot.retrievedAt ?? new Date().toISOString(),
      contributesTo: sectionIds.filter(s => s === 'siteResearch'),
    };
  }

  // ── Prior reports (RDX bridge). ───────────────────────────────
  const priorReportRows = extractPriorReportLineage(sectionIds, sed, priorReports);

  // ── LX-3 — live-literature channel. Coarse projection; the channel
  //          is present iff the LLW orchestrator returned 'ok' /
  //          'partial' for the (site, reportType). Not requirement-
  //          filtered (LL6 will own per-row relevance). ────────────
  let liveLiterature: LiveLiteratureLineage | undefined;
  const litSlot = sed?.externalLiterature;
  if (litSlot && (litSlot.status === 'ok' || litSlot.status === 'partial') && litSlot.entries.length > 0) {
    const top = litSlot.entries[0];
    liveLiterature = {
      entryCount: litSlot.entries.length,
      status: litSlot.status,
      fetchedAt: litSlot.fetchedAt,
      topEntry: top
        ? {
            title: top.title,
            provider: top.provider,
            url: top.url,
            year: top.year,
          }
        : undefined,
    };
  }

  // ── Prompt inclusion. ─────────────────────────────────────────
  const promptInclusion = computePromptInclusion(requirementId, sectionIds, authored, damLineage, sedSlots);

  // ── EVL-6 — sub-field coverage. ───────────────────────────────
  const fieldCoverage = computeRequirementFieldCoverage(requirementId, sed);

  // ── Counts. ──────────────────────────────────────────────────
  const counts: RequirementLineageCounts = {
    damSources: damLineage?.relevantReferences.length ?? 0,
    providerSources: sedSlots.filter(
      s => s.provenance === 'provider' || s.provenance === 'provider+doc' || s.provenance === 'doc-override',
    ).length,
    docSources: new Set(sedSlots.flatMap(s => s.docIds)).size,
    authoredClaims: authored.length,
    siteResearchSources: siteResearch ? 1 : 0,
    priorReportSources: priorReportRows.length,
  };

  return {
    requirementId,
    requirementLabel: requirementLabel ?? requirement.label ?? requirementId,
    reportType,
    status,
    isStrong: status === 'strong' || status === 'authored-strong',
    sources: {
      authored,
      sedSlots,
      dctMatches,
      dam: damLineage,
      siteResearch,
      priorReports: priorReportRows,
      liveLiterature,
    },
    promptInclusion,
    counts,
    fieldCoverage,
  };
}
